import jsPDF from 'jspdf';
import { BasePDFOptions } from '..';
import { SRBReport } from '@/types/pos/srb-report';
import { capitalizeFirstLetter } from '@/lib/helper';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

const formatDate = (value: string | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

export async function srbReportTemplate(
  options: BasePDFOptions<SRBReport[]>
): Promise<void> {
  const {
    data,
    filename = 'srb-report.pdf',
    title = 'SRB Tax Report',
    orientation = 'portrait',
    dateRange,
    summary,
  } = options;

  const pdf = new jsPDF(orientation === 'landscape' ? 'l' : 'p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  let yPosition = margin;

  // ── Header bar ──────────────────────────────────────────────────────────────
  pdf.setFillColor(30, 41, 59);
  pdf.rect(0, 0, pageWidth, 25, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, margin, 10);

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const formattedTime = currentDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  pdf.text(`Generated: ${formattedDate} at ${formattedTime}`, margin, 17);

  // Date range info on right
  if (dateRange?.start_date || dateRange?.end_date) {
    const rangeText = [dateRange.start_date, dateRange.end_date].filter(Boolean).join(' → ');
    pdf.text(`Period: ${rangeText}`, pageWidth - margin, 10, { align: 'right' });
  }
  pdf.text(`Total Records: ${summary?.totalCount || data.length}`, pageWidth - margin, 17, { align: 'right' });

  yPosition = 30;

  // ── Table ────────────────────────────────────────────────────────────────────
  // Columns match CSV: TXN Number, Tax Number, Date, Updated At, Net Price, Discount, Subtotal, Tax Rate (%), Tax Amount, Total Amount, Tax Type, Payment Method
  const headers = [
    'TXN #', 'Tax No.', 'Date', 'Updated At',
    'Net Price', 'Discount', 'Subtotal', 'Tax Rate %',
    'Tax Amt', 'Total', 'Tax Type', 'Payment',
  ];

  // Relative column widths (landscape vs portrait)
  const baseColWidths = orientation === 'landscape'
    ? [22, 22, 18, 18, 20, 16, 20, 14, 16, 18, 14, 22]
    : [16, 16, 14, 14, 16, 12, 16, 10, 12, 14, 11, 18];

  const availableWidth = pageWidth - margin * 2;
  const baseTableWidth = baseColWidths.reduce((s, w) => s + w, 0);
  const scaleFactor = availableWidth / baseTableWidth;
  const colWidths = baseColWidths.map(w => w * scaleFactor);
  const tableWidth = colWidths.reduce((s, w) => s + w, 0);
  const tableStartX = (pageWidth - tableWidth) / 2;

  const headerFontSize = orientation === 'landscape' ? 7.5 : 6;
  const dataFontSize   = orientation === 'landscape' ? 7   : 5.5;

  // Numeric column indices (right-aligned): Net Price, Discount, Subtotal, Tax Rate %, Tax Amt, Total
  const numericCols = new Set([4, 5, 6, 7, 8, 9]);

  const drawHeader = () => {
    pdf.setFillColor(241, 245, 249);
    pdf.rect(tableStartX, yPosition, tableWidth, 8, 'F');
    pdf.setDrawColor(203, 213, 225);
    pdf.setLineWidth(0.5);
    pdf.line(tableStartX, yPosition + 8, tableStartX + tableWidth, yPosition + 8);

    pdf.setTextColor(51, 65, 85);
    pdf.setFontSize(headerFontSize);
    pdf.setFont('helvetica', 'bold');
    let x = tableStartX;
    headers.forEach((header, i) => {
      if (numericCols.has(i)) {
        pdf.text(header, x + colWidths[i] - 2, yPosition + 5, { align: 'right' });
      } else {
        pdf.text(header, x + 2, yPosition + 5);
      }
      x += colWidths[i];
    });
    yPosition += 8;
  };

  drawHeader();

  // ── Rows ─────────────────────────────────────────────────────────────────────
  pdf.setFontSize(dataFontSize);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(15, 23, 42);
  const rowHeight = 7;
  let rowCount = 0;

  data.forEach((row: any) => {
    if (yPosition > pageHeight - 25) {
      pdf.addPage();
      yPosition = margin + 8;
      drawHeader();
      pdf.setFontSize(dataFontSize);
      pdf.setFont('helvetica', 'normal');
      rowCount = 0;
    }

    if (rowCount % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(tableStartX, yPosition, tableWidth, rowHeight, 'F');
    }

    const subtotal = Number(row.sub_total || 0) - Number(row.discount || 0);
    const paymentMethods = Array.from(
      new Set((row.payments || []).map((p: any) => p.payment_method).filter(Boolean))
    ).join(', ');

    const rowData: string[] = [
      truncateText(row.receipt_number || '', orientation === 'landscape' ? 16 : 12),
      truncateText(String(row.tax_number || ''), orientation === 'landscape' ? 16 : 12),
      formatDate(row.created_at),
      formatDate(row.updated_at),
      formatCurrency(Number(row.sub_total || 0)),
      Number(row.discount || 0) > 0 ? formatCurrency(Number(row.discount)) : '—',
      formatCurrency(subtotal),
      row.tax_rate != null ? String(row.tax_rate) : '—',
      formatCurrency(Number(row.tax_amount || 0)),
      formatCurrency(Number(row.total || 0)),
      truncateText(capitalizeFirstLetter(row.tax_type || ''), 10),
      truncateText(paymentMethods, orientation === 'landscape' ? 18 : 14),
    ];

    let x = tableStartX;
    rowData.forEach((cellValue, ci) => {
      const isNumeric = numericCols.has(ci);
      // Colour discount column red when > 0
      if (ci === 5 && cellValue !== '—') {
        pdf.setTextColor(239, 68, 68);
      } else if (isNumeric) {
        pdf.setTextColor(15, 23, 42);
      } else if (ci === 0) {
        pdf.setTextColor(30, 41, 59);
      } else {
        pdf.setTextColor(71, 85, 105);
      }
      const textY = yPosition + 5;
      if (isNumeric) {
        pdf.text(cellValue, x + colWidths[ci] - 2, textY, { align: 'right' });
      } else {
        pdf.text(cellValue, x + 2, textY);
      }
      x += colWidths[ci];
    });

    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.2);
    pdf.line(tableStartX, yPosition + rowHeight, tableStartX + tableWidth, yPosition + rowHeight);
    yPosition += rowHeight;
    rowCount++;
  });

  // ── Footer ───────────────────────────────────────────────────────────────────
  const totalPages = (pdf.internal as any).pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFillColor(249, 250, 251);
    pdf.rect(0, pageHeight - 14, pageWidth, 14, 'F');
    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.5);
    pdf.line(0, pageHeight - 14, pageWidth, pageHeight - 14);

    pdf.setTextColor(100, 116, 139);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text(
      'This is a computer-generated document. No signature is required.',
      pageWidth / 2,
      pageHeight - 9,
      { align: 'center' }
    );
    pdf.text(
      `SRB Tax Report | Total Records: ${data.length}`,
      pageWidth / 2,
      pageHeight - 5,
      { align: 'center' }
    );
    pdf.setTextColor(71, 85, 105);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 4, { align: 'right' });
  }

  pdf.save(filename);
}
